"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dialogButtonClass = exports.DialogFooter = exports.DialogHeader = exports.DialogShell = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * RedPanda UI - DialogShell / DialogFooter
 * 對話框外殼：遮罩、面板、Esc 關閉（docs/介面優化.md §3-17、R15）
 * - 面板比背景亮一階＋1px 邊框＋陰影，深色主題下邊界清楚
 * - 點遮罩、按 Esc、按取消三種方式都能關閉
 * - 開啟動畫：遮罩淡入＋面板淡入微放大（200ms，動畫定義在 tailwind-config）；遮罩原本瞬間變暗，看起來會閃
 * - 開著時鎖住背景捲動：overflow hidden；html 沒設 scrollbar-gutter: stable 時補上捲軸寬度，頁面不會因捲軸消失而左右跳
 * - 長表單：DialogHeader／DialogFooter 是 sticky，內容超過 90vh 時只捲中間欄位，標題與確定／取消一直看得到
 */
const react_1 = require("react");
const outline_1 = require("@heroicons/react/24/outline");
const utils_1 = require("../../utils");
// 多個對話框疊開（例如編輯視窗上再開確認視窗）時，最後一個關閉才解除鎖定
let scrollLockCount = 0;
let savedBodyStyle = { overflow: '', paddingRight: '' };
const lockBodyScroll = () => {
    if (scrollLockCount++ > 0)
        return;
    const { style } = document.body;
    const html = document.documentElement;
    const scrollbarWidth = window.innerWidth - html.clientWidth;
    // html 設了 scrollbar-gutter: stable（各系統 index.css）時捲軸位置一直保留，不用補；再補會讓右側內容往左跳
    const gutterReserved = getComputedStyle(html).scrollbarGutter.includes('stable');
    savedBodyStyle = { overflow: style.overflow, paddingRight: style.paddingRight };
    style.overflow = 'hidden';
    if (scrollbarWidth > 0 && !gutterReserved)
        style.paddingRight = `${scrollbarWidth}px`;
};
const unlockBodyScroll = () => {
    if (--scrollLockCount > 0)
        return;
    Object.assign(document.body.style, savedBodyStyle);
};
const DialogShell = ({ onClose, children, closeDisabled = false, className }) => {
    // 用 ref 存最新的 onClose／closeDisabled，避免父層每次 render 都重綁監聽
    const onCloseRef = (0, react_1.useRef)(onClose);
    const closeDisabledRef = (0, react_1.useRef)(closeDisabled);
    onCloseRef.current = onClose;
    closeDisabledRef.current = closeDisabled;
    // 在繪製前鎖定，避免先畫一格有捲軸的畫面
    (0, react_1.useLayoutEffect)(() => {
        lockBodyScroll();
        return unlockBodyScroll;
    }, []);
    (0, react_1.useEffect)(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape' || closeDisabledRef.current)
                return;
            // 下拉選單展開中：Esc 只關選單。要在捕獲階段檢查——React 18 會在事件冒泡到 document 前
            // 同步把選單收起，冒泡階段再查 aria-expanded 已經是 false
            if (document.querySelector('[role="combobox"][aria-expanded="true"]'))
                return;
            onCloseRef.current();
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, []);
    // 只有「在遮罩上按下」才關閉：在面板內按下、拖曳選字到遮罩放開時不會誤關（onClick 會）
    const handleOverlayMouseDown = () => {
        if (!closeDisabled)
            onClose();
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: [(0, jsx_runtime_1.jsx)("div", { className: "absolute inset-0 bg-black/60 animate-overlay-in motion-reduce:animate-none", onMouseDown: handleOverlayMouseDown, "aria-hidden": "true" }), (0, jsx_runtime_1.jsx)("div", { role: "dialog", "aria-modal": "true", className: (0, utils_1.cn)('relative w-full max-h-[90vh] overflow-auto rounded-lg border border-slate-200 bg-white text-left shadow-2xl dark:border-slate-700 dark:bg-slate-800 animate-dialog-in motion-reduce:animate-none', 
                // 預設寬度只在呼叫端沒指定 max-w-* 時才加：cn() 不處理 class 衝突，兩個 max-w 同時存在時
                // 誰生效取決於 Tailwind 產生 CSS 的順序（實測 max-w-md 會蓋掉 max-w-4xl）
                !/(^|\s)max-w-/.test(className ?? '') && 'max-w-md', className), children: children })] }));
};
exports.DialogShell = DialogShell;
exports.DialogShell.displayName = 'DialogShell';
// 標題列、按鈕列黏在面板（捲動容器）上下緣；底色要與面板相同，捲動的欄位才不會透出來
const stickyBar = 'sticky z-10 bg-white dark:bg-slate-800';
/** 表單對話框標題列：左標題、右 X；固定在面板頂端 */
const DialogHeader = ({ title, onClose, closeLabel = 'Close', closeDisabled = false }) => ((0, jsx_runtime_1.jsxs)("div", { className: (0, utils_1.cn)(stickyBar, 'top-0 flex items-center justify-between border-b border-slate-200 px-6 py-3 dark:border-slate-700'), children: [(0, jsx_runtime_1.jsx)("h2", { className: "text-lg font-semibold text-gray-900 dark:text-white", children: title }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: onClose, disabled: closeDisabled, className: "text-fg-muted transition-colors hover:text-fg disabled:opacity-50", "aria-label": closeLabel, children: (0, jsx_runtime_1.jsx)(outline_1.XMarkIcon, { className: "h-6 w-6" }) })] }));
exports.DialogHeader = DialogHeader;
exports.DialogHeader.displayName = 'DialogHeader';
/** 對話框底部按鈕列：靠右，左「取消」右主要動作；與內容同底色、上方細線分隔；固定在面板底端
 *  表單對話框把它放在 <form> 裡（最後一個子元素），Enter 送出照常運作 */
const DialogFooter = ({ children, className }) => ((0, jsx_runtime_1.jsx)("div", { className: (0, utils_1.cn)(stickyBar, 'bottom-0 flex justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700', className), children: children }));
exports.DialogFooter = DialogFooter;
exports.DialogFooter.displayName = 'DialogFooter';
/** 對話框按鈕樣式（取消＝次要框線；主要＝品牌色；危險＝紅） */
exports.dialogButtonClass = {
    cancel: 'rounded-md border border-slate-300 bg-transparent px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700',
    primary: 'rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50',
    danger: 'rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50',
};
